import { Star, StarHalf, Code, Award } from "lucide-react";
import { SectionTitle } from "../utils/helpers.jsx";

export default function PlatformPerformance({
  platformData,

  isTableView = false,
}) {
  console.log("platformData: ", platformData);
  // Check if platform data exists
  const hasPlatformData = (platform) => {
    if (!platformData) return false;
    return (
      platform in platformData &&
      platformData[platform] &&
      Object.keys(platformData[platform]).length > 0
    );
  };

  // Helper function to categorize difficulty or domain categories based on available data
  const renderCategoryCells = (platform, data) => {
    console.log("data: ", data);

    if (!data) return defaultCells();

    // For platforms with easy/medium/hard categories
    if (platform === "leetcode") {
      return (
        <>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded print:bg-green-50 print:font-bold print:py-0">
              {data.easy || 0}
            </span>
          </td>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium rounded text-amber-700 bg-amber-100 print:bg-amber-50 print:font-bold print:py-0">
              {data.medium || 0}
            </span>
          </td>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded print:bg-red-50 print:font-bold print:py-0">
              {data.hard || 0}
            </span>
          </td>
        </>
      );
    } else if (platform === "gfg") {
      return (
        <>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded print:bg-green-50 print:font-bold print:py-0">
              {data.EasyCount || 0}
            </span>
          </td>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium rounded text-amber-700 bg-amber-100 print:bg-amber-50 print:font-bold print:py-0">
              {data.MediumCount || 0}
            </span>
          </td>
          <td className="px-4 py-3 text-center border print:py-2">
            <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded print:bg-red-50 print:font-bold print:py-0">
              {data.HardCount || 0}
            </span>
          </td>
        </>
      );
    }
    // For HackerRank with domain categories
    else if (platform === "hackerrank") {
      return (
        <>
          <td className="px-4 py-3 text-center border print:py-2" colSpan="3">
            <div className="flex flex-col gap-1">
              {/* Always show Algorithms even if score is 0 */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Algorithms:</span>
                <span className="px-2 py-1 text-xs font-medium text-blue-700 bg-blue-100 rounded print:bg-blue-50 print:font-bold print:py-0">
                  {data.algorithms_score || 0}
                  {data.algorithms_global_rank > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      (Rank: {data.algorithms_global_rank})
                    </span>
                  )}
                </span>
              </div>

              {/* Always show Data Structures even if score is 0 */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Data Structures:</span>
                <span className="px-2 py-1 text-xs font-medium text-purple-700 bg-purple-100 rounded print:bg-purple-50 print:font-bold print:py-0">
                  {data.structures_score || 0}
                  {data.structures_global_rank > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      (Rank: {data.structures_global_rank})
                    </span>
                  )}
                </span>
              </div>

              {/* Always show Mathematics even if score is 0 */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium">Mathematics:</span>
                <span className="px-2 py-1 text-xs font-medium text-green-700 bg-green-100 rounded print:bg-green-50 print:font-bold print:py-0">
                  {data.mathematics_score || 0}
                  {data.mathematics_global_rank > 0 && (
                    <span className="ml-1 text-xs text-gray-600">
                      (Rank: {data.mathematics_global_rank})
                    </span>
                  )}
                </span>
              </div>
            </div>
          </td>
        </>
      );
    }
    // For CodeForces (customize based on your actual data structure)
    else if (platform === "codeforcesData") {
      return defaultCells("No category data available");
    }
    // For other platforms without specific categories
    else {
      return defaultCells();
    }
  };

  // Default empty cells
  const defaultCells = (message = "-") => (
    <>
      <td className="px-4 py-3 text-center border">{message}</td>
      <td className="px-4 py-3 text-center border">{message}</td>
      <td className="px-4 py-3 text-center border">{message}</td>
    </>
  );

  // Safe data getter function with default values
  const getPlatformData = (platform, prop, defaultValue = "-") => {
    try {
      if (platform === "leetcode") {
        if (
          platformData.leetcodeLeaderboard &&
          platformData.leetcodeLeaderboard[0]
        ) {
          if (prop === "problemsSolved")
            return (
              platformData.leetcodeLeaderboard[0]["All count"] || defaultValue
            );
          if (prop === "easy")
            return (
              platformData.leetcodeLeaderboard[0]["Easy count"] || defaultValue
            );
          if (prop === "medium")
            return (
              platformData.leetcodeLeaderboard[0]["Medium count"] ||
              defaultValue
            );
          if (prop === "hard")
            return (
              platformData.leetcodeLeaderboard[0]["Hard count"] || defaultValue
            );
          if (prop === "badges")
            return platformData.leetcodeLeaderboard[0].badge || defaultValue;
        }
        // Fallback to old structure if available
        else if (
          platformData.leetcode &&
          platformData.leetcode[prop] !== undefined
        ) {
          return platformData.leetcode[prop];
        }
        return defaultValue;
      } else if (platform === "hackerrank") {
        if (
          platformData.hackerrank_leaderboard &&
          platformData.hackerrank_leaderboard[0]
        ) {
          return platformData.hackerrank_leaderboard[0][prop] !== undefined
            ? platformData.hackerrank_leaderboard[0][prop]
            : defaultValue;
        } else if (
          platformData.hackerrank &&
          platformData.hackerrank[prop] !== undefined
        ) {
          return platformData.hackerrank[prop];
        }
        return defaultValue;
      } else if (platform === "gfg") {
        if (platformData.Gfgprofile && platformData.Gfgprofile[0]) {
          return platformData.Gfgprofile[0][prop] !== undefined
            ? platformData.Gfgprofile[0][prop]
            : defaultValue;
        } else if (platformData.gfg && platformData.gfg[prop] !== undefined) {
          return platformData.gfg[prop];
        }
        return defaultValue;
      } else if (platform === "codeforcesData") {
        if (
          platformData.codeforcesData &&
          platformData.codeforcesData[prop] !== undefined
        ) {
          return platformData.codeforcesData[prop];
        } else if (
          platformData.codeforcesData &&
          platformData.codeforcesData.leaderboard &&
          platformData.codeforcesData.leaderboard[0]
        ) {
          return platformData.codeforcesData.leaderboard[0][prop] !== undefined
            ? platformData.codeforcesData.leaderboard[0][prop]
            : defaultValue;
        }
        return defaultValue;
      }
      return defaultValue;
    } catch (error) {
      console.error(`Error getting ${prop} from ${platform}:`, error);
      return defaultValue;
    }
  };

  // Helper function to determine if a platform has data
  const platformHasData = (platformName) => {
    if (platformName === "leetcode") {
      return !!(
        platformData.leetcodeLeaderboard?.length ||
        (platformData.leetcode && Object.keys(platformData.leetcode).length > 0)
      );
    }
    if (platformName === "hackerrank") {
      return !!(
        platformData.hackerrank_leaderboard?.length ||
        (platformData.hackerrank &&
          Object.keys(platformData.hackerrank).length > 0)
      );
    }
    if (platformName === "gfg") {
      return !!(
        platformData.Gfgprofile?.length ||
        (platformData.gfg && Object.keys(platformData.gfg).length > 0)
      );
    }
    if (platformName === "codeforcesData") {
      return !!(
        platformData.codeforcesData?.leaderboard?.length ||
        (platformData.codeforcesData &&
          platformData.codeforcesData.problems_solved)
      );
    }
    return false;
  };

  // Table view format
  if (isTableView) {
    return (
      <div className="mb-6 md:mb-8">
        <SectionTitle title="💻 Coding Platform Performance" />
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-2 font-medium text-left border text-slate-600 w-[15%]">
                  Platform
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600 w-[15%]">
                  Problems Solved
                </th>
                <th
                  className="px-4 py-2 font-medium text-left border text-slate-600 w-[45%]"
                  colSpan="3"
                >
                  Difficulty / Categories
                </th>
                <th className="px-4 py-2 font-medium text-left border text-slate-600 w-[25%]">
                  Additional Info
                </th>
              </tr>
            </thead>
            <tbody>
              {/* LeetCode */}
              <tr>
                <td className="px-4 py-3 font-medium border">LeetCode</td>
                <td className="px-4 py-3 text-center border">
                  {getPlatformData("leetcode", "problemsSolved")}
                </td>
                {renderCategoryCells("leetcode", {
                  easy: getPlatformData("leetcode", "easy", 0),
                  medium: getPlatformData("leetcode", "medium", 0),
                  hard: getPlatformData("leetcode", "hard", 0),
                })}
                <td className="px-4 py-3 border">
                  Badges Earned: {getPlatformData("leetcode", "badges", "-")}
                </td>
              </tr>

              {/* HackerRank */}
              <tr>
                <td className="px-4 py-3 font-medium border">HackerRank</td>
                <td className="px-4 py-3 text-center border">
                  {/* HackerRank doesn't provide a direct "problems solved" count */}
                  -
                </td>
                {renderCategoryCells("hackerrank", {
                  algorithms_score: getPlatformData(
                    "hackerrank",
                    "algorithms_score"
                  ),
                  algorithms_global_rank: getPlatformData(
                    "hackerrank",
                    "algorithms_global_rank",
                    0
                  ),
                  structures_score: getPlatformData(
                    "hackerrank",
                    "structures_score"
                  ),
                  structures_global_rank: getPlatformData(
                    "hackerrank",
                    "structures_global_rank",
                    0
                  ),
                  mathematics_score: getPlatformData(
                    "hackerrank",
                    "mathematics_score"
                  ),
                  mathematics_global_rank: getPlatformData(
                    "hackerrank",
                    "mathematics_global_rank",
                    0
                  ),
                })}
                <td className="px-4 py-3 border">
                  <div className="flex items-center gap-2">
                    <span>
                      Score: {getPlatformData("hackerrank", "total_score", 0)} |
                      Stars:{" "}
                    </span>
                    {[
                      ...Array(
                        Math.floor(
                          Number(getPlatformData("hackerrank", "stars", 0))
                        )
                      ),
                    ].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 fill-amber-400 text-amber-400"
                      />
                    ))}
                    {Number(getPlatformData("hackerrank", "stars", 0)) % 1 !==
                      0 && (
                      <StarHalf className="w-4 h-4 fill-amber-400 text-amber-400" />
                    )}
                    {getPlatformData("hackerrank", "badge") !== "-" && (
                      <span className="ml-2 text-xs">
                        | Badge: {getPlatformData("hackerrank", "badge")}
                      </span>
                    )}
                  </div>
                </td>
              </tr>

              {/* CodeForces */}
              <tr>
                <td className="px-4 py-3 font-medium border">CodeForces</td>
                <td className="px-4 py-3 text-center border">
                  {getPlatformData("codeforcesData", "problems_solved", "-")}
                </td>
                {renderCategoryCells(
                  "codeforcesData",
                  platformData.codeforcesData
                )}
                <td className="px-4 py-3 border">-</td>
              </tr>

              {/* GeeksForGeeks */}
              <tr>
                <td className="px-4 py-3 font-medium border">GeeksForGeeks</td>
                <td className="px-4 py-3 text-center border">
                  {getPlatformData("gfg", "problems_solved", "-")}
                </td>
                {renderCategoryCells("gfg", {
                  EasyCount: getPlatformData("gfg", "EasyCount", 0),
                  MediumCount: getPlatformData("gfg", "MediumCount", 0),
                  HardCount: getPlatformData("gfg", "HardCount", 0),
                })}
                <td className="px-4 py-3 border">-</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Original card view
  return (
    <div className="mb-6 md:mb-8">
      <SectionTitle title="Platform Performance" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* LeetCode */}
        {platformHasData("leetcode") && (
          <div className="flex flex-col p-3 border border-gray-200 rounded-lg shadow-sm md:p-4">
            <h3 className="flex items-center gap-2 mb-3 text-base font-semibold md:text-lg">
              <Code className="w-5 h-5 text-blue-600" />
              LeetCode
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs md:text-sm text-slate-500">
                  Problems Solved
                </p>
                <p className="text-lg font-bold text-blue-600 break-words md:text-2xl">
                  {getPlatformData("leetcode", "problemsSolved", 0)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs md:text-sm text-slate-500">
                  Difficulty Breakdown
                </p>
                <div className="grid grid-cols-3 gap-1 text-xs break-words md:gap-2 md:text-sm">
                  <div className="p-2 text-center border border-green-100 rounded bg-green-50">
                    <p className="font-medium text-green-600 break-words">
                      Easy: {getPlatformData("leetcode", "easy", 0)}
                    </p>
                  </div>
                  <div className="p-2 text-center border rounded bg-amber-50 border-amber-100">
                    <p className="font-medium break-words text-amber-600">
                      Medium: {getPlatformData("leetcode", "medium", 0)}
                    </p>
                  </div>
                  <div className="p-2 text-center border border-red-100 rounded bg-red-50">
                    <p className="font-medium text-red-600 break-words">
                      Hard: {getPlatformData("leetcode", "hard", 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-xs md:text-sm text-slate-500">
                  Badges Earned
                </p>
                <p className="text-sm font-bold break-words md:text-lg text-slate-900">
                  {getPlatformData("leetcode", "badges", "-")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* HackerRank */}
        {platformHasData("hackerrank") && (
          <div className="flex flex-col p-3 border border-gray-200 rounded-lg shadow-sm md:p-4">
            <h3 className="flex items-center gap-2 mb-3 text-base font-semibold md:text-lg">
              <Award className="w-5 h-5 text-blue-600" />
              HackerRank
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs md:text-sm text-slate-500">Total Score</p>
                <p className="text-lg font-bold text-blue-600 break-words md:text-2xl">
                  {getPlatformData("hackerrank", "total_score", 0)}
                </p>
              </div>
              {/* Domain-specific categories */}
              <div>
                <p className="mb-1 text-xs md:text-sm text-slate-500">
                  Domain Categories
                </p>
                <div className="grid grid-cols-1 gap-1 text-xs break-words md:gap-2 md:text-sm">
                  {/* Always show Algorithms */}
                  <div className="p-2 text-center border border-blue-100 rounded bg-blue-50">
                    <p className="font-medium text-blue-600 break-words">
                      Algorithms:{" "}
                      {getPlatformData("hackerrank", "algorithms_score", 0)}
                      {Number(
                        getPlatformData(
                          "hackerrank",
                          "algorithms_global_rank",
                          0
                        )
                      ) > 0 && (
                        <span className="text-xs text-gray-500">
                          {" "}
                          (Rank:{" "}
                          {getPlatformData(
                            "hackerrank",
                            "algorithms_global_rank",
                            0
                          )}
                          )
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Always show Data Structures */}
                  <div className="p-2 text-center border border-purple-100 rounded bg-purple-50">
                    <p className="font-medium text-purple-600 break-words">
                      Data Structures:{" "}
                      {getPlatformData("hackerrank", "structures_score", 0)}
                      {Number(
                        getPlatformData(
                          "hackerrank",
                          "structures_global_rank",
                          0
                        )
                      ) > 0 && (
                        <span className="text-xs text-gray-500">
                          {" "}
                          (Rank:{" "}
                          {getPlatformData(
                            "hackerrank",
                            "structures_global_rank",
                            0
                          )}
                          )
                        </span>
                      )}
                    </p>
                  </div>

                  {/* Always show Mathematics */}
                  <div className="p-2 text-center border border-green-100 rounded bg-green-50">
                    <p className="font-medium text-green-600 break-words">
                      Mathematics:{" "}
                      {getPlatformData("hackerrank", "mathematics_score", 0)}
                      {Number(
                        getPlatformData(
                          "hackerrank",
                          "mathematics_global_rank",
                          0
                        )
                      ) > 0 && (
                        <span className="text-xs text-gray-500">
                          {" "}
                          (Rank:{" "}
                          {getPlatformData(
                            "hackerrank",
                            "mathematics_global_rank",
                            0
                          )}
                          )
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div>
                  <p className="mb-1 text-xs md:text-sm text-slate-500">
                    Stars
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {[
                      ...Array(
                        Math.floor(
                          Number(getPlatformData("hackerrank", "stars", 0))
                        )
                      ),
                    ].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-amber-400 text-amber-400"
                      />
                    ))}
                    {Number(getPlatformData("hackerrank", "stars", 0)) % 1 !==
                      0 && (
                      <StarHalf className="w-5 h-5 fill-amber-400 text-amber-400" />
                    )}
                  </div>
                </div>
                {getPlatformData("hackerrank", "badge") !== "-" && (
                  <div>
                    <p className="text-xs md:text-sm text-slate-500">Badge</p>
                    <p className="text-sm font-semibold text-slate-700">
                      {getPlatformData("hackerrank", "badge")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* GeeksForGeeks */}
        {platformHasData("gfg") && (
          <div className="flex flex-col p-3 border border-gray-200 rounded-lg shadow-sm md:p-4">
            <h3 className="flex items-center gap-2 mb-3 text-base font-semibold md:text-lg">
              <Code className="w-5 h-5 text-blue-600" />
              GeeksForGeeks
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs md:text-sm text-slate-500">
                  Problems Solved
                </p>
                <p className="text-lg font-bold text-blue-600 break-words md:text-2xl">
                  {getPlatformData("gfg", "problems_solved", 0)}
                </p>
              </div>
              <div>
                <p className="mb-1 text-xs md:text-sm text-slate-500">
                  Difficulty Breakdown
                </p>
                <div className="grid grid-cols-3 gap-1 text-xs md:gap-2 md:text-sm">
                  <div className="p-2 text-center border border-green-100 rounded bg-green-50">
                    <p className="font-medium text-green-600 break-words">
                      Easy: {getPlatformData("gfg", "EasyCount", 0)}
                    </p>
                  </div>
                  <div className="p-2 text-center border rounded bg-amber-50 border-amber-100">
                    <p className="font-medium break-words text-amber-600">
                      Medium: {getPlatformData("gfg", "MediumCount", 0)}
                    </p>
                  </div>
                  <div className="p-2 text-center border border-red-100 rounded bg-red-50">
                    <p className="font-medium text-red-600 break-words">
                      Hard: {getPlatformData("gfg", "HardCount", 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CodeForces */}
        {platformHasData("codeforcesData") && (
          <div className="flex flex-col p-3 border border-gray-200 rounded-lg shadow-sm md:p-4">
            <h3 className="flex items-center gap-2 mb-3 text-base font-semibold md:text-lg">
              <Code className="w-5 h-5 text-blue-600" />
              CodeForces
            </h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs md:text-sm text-slate-500">
                  Problems Solved
                </p>
                <p className="text-lg font-bold text-blue-600 break-words md:text-2xl">
                  {getPlatformData("codeforcesData", "problems_solved", 0)}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
